import React, { useEffect, useState } from 'react';
import { useApiToken } from 'components/hooks';
import { Alert, ModalAlert } from 'components/common';
import ApiTokenListDumb from './ApiTokenListDumb';

const ApiTokenListSmart = () => {
  const apiToken = useApiToken();

  // ── State ──────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState(0);

  // Clients
  const [clients, setClients] = useState([]);
  const [openClientModal, setOpenClientModal] = useState(false);
  const [openDeleteClientModal, setOpenDeleteClientModal] = useState(false);
  const [clientSelected, setClientSelected] = useState(null);

  // Tokens
  const [tokens, setTokens] = useState([]);
  const [openTokenModal, setOpenTokenModal] = useState(false);
  const [openRevokeModal, setOpenRevokeModal] = useState(false);
  const [openDeleteTokenModal, setOpenDeleteTokenModal] = useState(false);
  const [tokenSelected, setTokenSelected] = useState(null);
  const [newPlainToken, setNewPlainToken] = useState(null);
  const [filterClientId, setFilterClientId] = useState('');

  // Audit
  const [auditLog, setAuditLog] = useState([]);
  const [auditFilterClientId, setAuditFilterClientId] = useState('');

  // ── Alerts ─────────────────────────────────────────────────────────────────
  const alerts = [
    { severity: 'success', open: apiToken.success.post,   message: 'apiTokenList.components.alert.success' },
    { severity: 'success', open: apiToken.success.delete, message: 'apiTokenList.components.alert.deleteSuccess' },
    { severity: 'success', open: apiToken.success.put,    message: 'apiTokenList.components.alert.revokeSuccess' },
    { severity: 'error',   open: !!apiToken.error.post,   message: 'apiTokenList.components.alert.error' },
    { severity: 'error',   open: !!apiToken.error.delete, message: 'apiTokenList.components.alert.error' },
    { severity: 'error',   open: !!apiToken.error.put,    message: 'apiTokenList.components.alert.error' },
  ];

  // ── Loaders ────────────────────────────────────────────────────────────────
  const loadClients = () => {
    apiToken.getClients({});
  };

  const loadTokens = (clientId) => {
    apiToken.getTokens({ query: clientId ? { client_id: clientId } : {} });
  };

  const loadAuditLog = (clientId) => {
    apiToken.getAuditLog({ query: clientId ? { client_id: clientId, limit: 200 } : { limit: 200 } });
  };

  // ── Tab change ─────────────────────────────────────────────────────────────
  const handleTabChange = (_, newTab) => {
    setTab(newTab);
    apiToken.cleanUpMessage();
    if (newTab === 0) loadClients();
    if (newTab === 1) loadTokens(filterClientId);
    if (newTab === 2) loadAuditLog(auditFilterClientId);
  };

  // ── Clients ────────────────────────────────────────────────────────────────
  const handleCreateClient = (formData) => {
    apiToken.postClient({ body: { name: formData.name, description: formData.description } });
    setOpenClientModal(false);
  };

  const handleToggleClient = (client) => {
    apiToken.toggleClient({ body: { client_id: client.client_id, is_active: !client.is_active } });
  };

  const handleDeleteClient = () => {
    if (!clientSelected) return;
    apiToken.deleteClient({ query: { client_id: clientSelected.client_id } });
    setOpenDeleteClientModal(false);
    setClientSelected(null);
  };

  const handleOpenDeleteClient = (client) => {
    setClientSelected(client);
    setOpenDeleteClientModal(true);
  };

  // ── Tokens ─────────────────────────────────────────────────────────────────
  const handleCreateToken = (formData) => {
    const scopes = formData.scopes || ['jobs.run'];
    const allowed_projects = Array.isArray(formData.allowed_projects)
      ? formData.allowed_projects
      : formData.allowed_projects
        ? formData.allowed_projects.split(',').map((s) => parseInt(s.trim(), 10)).filter(Boolean)
        : [];
    const allowed_templates = Array.isArray(formData.allowed_templates)
      ? formData.allowed_templates
      : formData.allowed_templates
        ? formData.allowed_templates.split(',').map((s) => parseInt(s.trim(), 10)).filter(Boolean)
        : [];
    apiToken.postToken({
      body: {
        client_id: formData.client_id,
        scopes,
        allowed_projects,
        allowed_templates,
        expires_days: parseInt(formData.expires_days, 10) || 365,
      },
    });
    setOpenTokenModal(false);
  };

  const handleRevokeToken = () => {
    if (!tokenSelected) return;
    apiToken.revokeToken({ body: { token_id: tokenSelected.token_id } });
    setOpenRevokeModal(false);
    setTokenSelected(null);
  };

  const handleOpenRevokeToken = (token) => {
    setTokenSelected(token);
    setOpenRevokeModal(true);
  };

  const handleDeleteToken = () => {
    if (!tokenSelected) return;
    apiToken.deleteToken({ body: { token_id: tokenSelected.token_id } });
    setOpenDeleteTokenModal(false);
    setTokenSelected(null);
  };

  const handleOpenDeleteToken = (token) => {
    setTokenSelected(token);
    setOpenDeleteTokenModal(true);
  };

  const handleFilterClientTokens = (clientId) => {
    setFilterClientId(clientId);
    loadTokens(clientId);
  };

  // ── Audit ──────────────────────────────────────────────────────────────────
  const handleFilterAudit = (clientId) => {
    setAuditFilterClientId(clientId);
    loadAuditLog(clientId);
  };

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadClients();
    return () => apiToken.cleanUp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track data returned and split by context
  useEffect(() => {
    if (!apiToken.data) return;
    const d = apiToken.data;
    if (tab === 0 && Array.isArray(d)) setClients(d);
    if (tab === 1 && Array.isArray(d)) setTokens(d);
    if (tab === 2 && Array.isArray(d)) setAuditLog(d);
    // When creating a token, store the plain token to show in modal
    if (tab === 1 && d?.plain_token) {
      setNewPlainToken(d.plain_token);
      loadTokens(filterClientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiToken.data]);

  useEffect(() => {
    if (apiToken.success.get && tab === 0) loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiToken.success.post, apiToken.success.delete, apiToken.success.put]);

  // After a successful mutation on the tokens tab, refresh the list so
  // the row reflects the new state immediately. Revoke keeps the row
  // visible (with a "Revoked" badge); delete removes it.
  useEffect(() => {
    if (tab === 1 && (apiToken.success.put || apiToken.success.delete)) {
      loadTokens(filterClientId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiToken.success.put, apiToken.success.delete, tab]);

  const handleCloseNewToken = () => setNewPlainToken(null);

  return (
    <>
      {alerts.map((alert, i) => (
        <Alert
          key={i}
          severity={alert.severity}
          open={alert.open}
          message={alert.message}
          handleClose={apiToken.cleanUpMessage}
        />
      ))}

      <ModalAlert
        open={openDeleteClientModal}
        handleClose={() => setOpenDeleteClientModal(false)}
        handleConfirm={handleDeleteClient}
        title='apiTokenList.components.modal.deleteClient.title'
        text='apiTokenList.components.modal.deleteClient.text'
      />

      <ModalAlert
        open={openRevokeModal}
        handleClose={() => setOpenRevokeModal(false)}
        handleConfirm={handleRevokeToken}
        title='apiTokenList.components.modal.revokeToken.title'
        text='apiTokenList.components.modal.revokeToken.text'
      />

      <ModalAlert
        open={openDeleteTokenModal}
        handleClose={() => setOpenDeleteTokenModal(false)}
        handleConfirm={handleDeleteToken}
        title='apiTokenList.components.modal.deleteToken.title'
        text='apiTokenList.components.modal.deleteToken.text'
      />

      <ApiTokenListDumb
        tab={tab}
        onTabChange={handleTabChange}
        // clients
        clients={clients}
        openClientModal={openClientModal}
        onOpenClientModal={() => setOpenClientModal(true)}
        onCloseClientModal={() => setOpenClientModal(false)}
        onCreateClient={handleCreateClient}
        onToggleClient={handleToggleClient}
        onDeleteClient={handleOpenDeleteClient}
        // tokens
        tokens={tokens}
        clients_for_select={clients}
        filterClientId={filterClientId}
        onFilterClientTokens={handleFilterClientTokens}
        openTokenModal={openTokenModal}
        onOpenTokenModal={() => {
          apiToken.getProjects({});
          apiToken.getTemplates({});
          setOpenTokenModal(true);
        }}
        onCloseTokenModal={() => setOpenTokenModal(false)}
        onCreateToken={handleCreateToken}
        onRevokeToken={handleOpenRevokeToken}
        onDeleteToken={handleOpenDeleteToken}
        newPlainToken={newPlainToken}
        onCloseNewToken={handleCloseNewToken}
        // projects & templates
        projects={apiToken.projects}
        templates={apiToken.templates}
        // audit
        auditLog={auditLog}
        auditFilterClientId={auditFilterClientId}
        onFilterAudit={handleFilterAudit}
        clients_for_audit={clients}
        // shared
        isLoaded={apiToken.isLoaded}
      />
    </>
  );
};

export default ApiTokenListSmart;
